import React, { useState } from "react";
import { useTheme } from "../context/ThemeContext";

function TradeForm({
  newTrade,
  setNewTrade,
  handleSubmit,
  isEditing,
  onCancelEdit,
}) {
  const { darkMode } = useTheme();
  const today = new Date().toISOString().split("T")[0];
  const [validationError, setValidationError] = useState("");

  // Validate price relationships
  const validatePrices = (trade, isSubmitting = false) => {
    if (
      isSubmitting &&
      (!trade.entryPrice || !trade.takeProfit || !trade.stopLoss)
    ) {
      setValidationError("All price fields are required");
      return false;
    }

    // Only validate relationships if all fields are filled
    if (trade.entryPrice && trade.takeProfit && trade.stopLoss) {
      const entry = parseFloat(trade.entryPrice);
      const tp = parseFloat(trade.takeProfit);
      const sl = parseFloat(trade.stopLoss);

      if (isNaN(entry) || isNaN(tp) || isNaN(sl)) {
        setValidationError("Invalid price values");
        return false;
      }

      if (trade.entryType === "buy") {
        if (tp <= entry) {
          setValidationError(`BUY: TP must be above ${entry}`);
          return false;
        }
        if (sl >= entry) {
          setValidationError(`BUY: SL must be below ${entry}`);
          return false;
        }
      } else {
        if (tp >= entry) {
          setValidationError(`SELL: TP must be below ${entry}`);
          return false;
        }
        if (sl <= entry) {
          setValidationError(`SELL: SL must be above ${entry}`);
          return false;
        }
      }
    }
    setValidationError("");
    return true;
  };

  // Automatically calculate outcome based on prices and direction
  const calculateOutcome = (trade) => {
    if (!trade.entryPrice || !trade.takeProfit || !trade.stopLoss)
      return trade.outcome;

    const entry = parseFloat(trade.entryPrice);
    const tp = parseFloat(trade.takeProfit);
    const sl = parseFloat(trade.stopLoss);

    if (trade.entryType === "buy") {
      if (tp <= entry || sl >= entry) return trade.outcome; // Invalid setup, keep current outcome
      return Math.abs(tp - entry) < Math.abs(entry - sl) ? "sl" : "tp";
    } else {
      if (tp >= entry || sl <= entry) return trade.outcome; // Invalid setup, keep current outcome
      return Math.abs(entry - tp) < Math.abs(sl - entry) ? "sl" : "tp";
    }
  };

  // Handle price changes and auto-calculate outcome
  const handlePriceChange = (field, value) => {
    const updatedTrade = { ...newTrade, [field]: value };
    validatePrices(updatedTrade);
    if (!validationError) {
      updatedTrade.outcome = calculateOutcome(updatedTrade);
    }
    setNewTrade(updatedTrade);
  };

  // Handle trade direction change
  const handleDirectionChange = (direction) => {
    const updatedTrade = { ...newTrade, entryType: direction };
    validatePrices(updatedTrade);
    if (!validationError) {
      updatedTrade.outcome = calculateOutcome(updatedTrade);
    }
    setNewTrade(updatedTrade);
  };

  // Handle form submission with validation
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!validatePrices(newTrade, true)) {
      return;
    }
    handleSubmit(e);
  };

  return (
    <form
      onSubmit={handleFormSubmit}
      className={`${
        darkMode ? "bg-gray-800" : "bg-white"
      } p-6 rounded-lg shadow-md mb-8`}
    >
      <h2
        className={`text-xl font-semibold mb-4 ${
          darkMode ? "text-white" : "text-gray-800"
        }`}
      >
        {isEditing ? "Edit Trade" : "Add New Trade"}
      </h2>

      {validationError && (
        <div className="mb-4 p-3 rounded bg-red-100 border border-red-400 text-red-700">
          {validationError}
        </div>
      )}

      <div className="space-y-4">
        {/* Trade Date */}
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${
              darkMode ? "text-gray-200" : "text-gray-700"
            }`}
          >
            Trade Date
          </label>
          <input
            type="date"
            value={newTrade.date}
            onChange={(e) => setNewTrade({ ...newTrade, date: e.target.value })}
            max={today}
            className={`border p-2 rounded w-full ${
              darkMode
                ? "bg-gray-700 border-gray-600 text-white"
                : "bg-white border-gray-300"
            }`}
            required
          />
        </div>

        {/* Trade Direction */}
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${
              darkMode ? "text-gray-200" : "text-gray-700"
            }`}
          >
            Trade Direction
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleDirectionChange("buy")}
              className={`p-2 rounded text-center font-medium transition-colors duration-150 ${
                newTrade.entryType === "buy"
                  ? "bg-green-500 text-white"
                  : darkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              BUY
            </button>
            <button
              type="button"
              onClick={() => handleDirectionChange("sell")}
              className={`p-2 rounded text-center font-medium transition-colors duration-150 ${
                newTrade.entryType === "sell"
                  ? "bg-red-500 text-white"
                  : darkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              SELL
            </button>
          </div>
        </div>

        {/* Entry Price */}
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${
              darkMode ? "text-gray-200" : "text-gray-700"
            }`}
          >
            Entry Price
          </label>
          <input
            type="number"
            value={newTrade.entryPrice}
            onChange={(e) => handlePriceChange("entryPrice", e.target.value)}
            className={`border p-2 rounded w-full ${
              darkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 placeholder-gray-500"
            }`}
            placeholder="Entry price"
            step="0.00001"
            required
          />
        </div>

        {/* Take Profit */}
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${
              darkMode ? "text-gray-200" : "text-gray-700"
            }`}
          >
            Take Profit
          </label>
          <input
            type="number"
            value={newTrade.takeProfit}
            onChange={(e) => handlePriceChange("takeProfit", e.target.value)}
            className={`border p-2 rounded w-full ${
              darkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 placeholder-gray-500"
            }`}
            placeholder="Take profit price"
            step="0.00001"
            required
          />
        </div>

        {/* Stop Loss */}
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${
              darkMode ? "text-gray-200" : "text-gray-700"
            }`}
          >
            Stop Loss
          </label>
          <input
            type="number"
            value={newTrade.stopLoss}
            onChange={(e) => handlePriceChange("stopLoss", e.target.value)}
            className={`border p-2 rounded w-full ${
              darkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 placeholder-gray-500"
            }`}
            placeholder="Stop loss price"
            step="0.00001"
            required
          />
        </div>

        {/* Lot Size */}
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${
              darkMode ? "text-gray-200" : "text-gray-700"
            }`}
          >
            Lot Size
          </label>
          <input
            type="number"
            value={newTrade.lotSize}
            onChange={(e) =>
              setNewTrade({ ...newTrade, lotSize: e.target.value })
            }
            className={`border p-2 rounded w-full ${
              darkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 placeholder-gray-500"
            }`}
            placeholder="Enter lot size"
            step="0.01"
            required
          />
        </div>

        {/* Trade Outcome (Manual Override) */}
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${
              darkMode ? "text-gray-200" : "text-gray-700"
            }`}
          >
            Trade Outcome (Auto-calculated, click to override)
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setNewTrade({ ...newTrade, outcome: "tp" })}
              className={`p-2 rounded text-center font-medium transition-colors duration-150 ${
                newTrade.outcome === "tp"
                  ? "bg-green-500 text-white"
                  : darkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              WIN (Hit TP)
            </button>
            <button
              type="button"
              onClick={() => setNewTrade({ ...newTrade, outcome: "sl" })}
              className={`p-2 rounded text-center font-medium transition-colors duration-150 ${
                newTrade.outcome === "sl"
                  ? "bg-red-500 text-white"
                  : darkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              LOSS (Hit SL)
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          type="submit"
          className={`flex-1 px-4 py-2 rounded font-medium transition-colors duration-150 ${
            validationError
              ? darkMode
                ? "bg-gray-600 cursor-not-allowed opacity-50"
                : "bg-gray-400 cursor-not-allowed opacity-50"
              : darkMode
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-blue-500 hover:bg-blue-600"
          } text-white`}
          disabled={!!validationError}
        >
          {isEditing ? "Update Trade" : "Add Trade"}
        </button>
        {isEditing && (
          <button
            type="button"
            onClick={onCancelEdit}
            className={`flex-1 px-4 py-2 rounded font-medium transition-colors duration-150 ${
              darkMode
                ? "bg-gray-600 hover:bg-gray-700"
                : "bg-gray-500 hover:bg-gray-600"
            } text-white`}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export default TradeForm;
